import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/hooks/useAuth';
import { Challenge } from './types';
import challengesService from '../../services/challengesService';
import { supabase } from '../../lib/supabase';
import ChallengeDisplay from './components/ChallengeDisplay';
import ChallengeInput from './components/ChallengeInput';
import ChallengeFeedback from './components/ChallengeFeedback';
import RankingList from './components/RankingList';
import { FaUser } from 'react-icons/fa';
import { FiHelpCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import HelpButton from '../../components/HelpButton';
import './ChallengesPage.css';

export default function ChallengesPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({ isVisible: false });
  const [rankingUsers, setRankingUsers] = useState<any[]>([]);

  useEffect(() => {
    if (session?.user?.id) {
      loadInitialData();
    }
  }, [session?.user?.id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load user profile
      const { data: profile, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', session!.user!.id)
        .single();

      if (error) throw error;
      setUserProfile(profile);

      // Load challenges
      await loadChallenges(profile.elo_rating || 1000);

      // Load ranking
      const { data: ranking, error: rankingError } = await supabase
        .from('users_ordered_by_elo')
        .select('*')
        .limit(10);

      if (rankingError) throw rankingError;
      setRankingUsers(ranking || []);

    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChallenges = async (currentElo: number) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_random_challenge', {
        current_elo: currentElo,
        elo_range: 200,
        exclude_id: '00000000-0000-0000-0000-000000000000'
      });

      if (error) throw error;

      const challengesData = Array.isArray(data) ? data : [data];
      setChallenges(challengesData);
    } catch (err) {
      console.error('Error loading challenges:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase.rpc('submit_challenge', {
        p_answer: userAnswer,
        p_challenge_id: challenges[0]?.id,
        p_user_id: session!.user!.id
      });

      if (error) throw error;

      const result = data[0]; // Response is an array with one item

      // Update user profile with new ELO if available
      if (result?.new_elo) {
        setUserProfile(prev => ({
          ...prev,
          previous_elo: prev?.elo_rating,
          elo_rating: result.new_elo
        }));
      }

      setFeedback({
        isVisible: true,
        isSuccess: result.is_correct,
        explanation: result.explanation,
        solution: result.solution,
        resources: result.resources
      });
    } catch (err) {
      console.error('Error submitting challenge:', err);
      setFeedback({
        isVisible: true,
        isSuccess: false,
        explanation: 'Ocorreu um erro ao enviar sua resposta. Por favor, tente novamente.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    setUserAnswer('');
    setFeedback({ isVisible: false });
    await loadChallenges(userProfile?.elo_rating || 1000);
  };

  if (loading) {
    return <div className="flex flex-col h-screen items-center justify-center mt-12 sm:mt-16 bg-base-300">
      <div className="relative">
        <div className="loading loading-spinner loading-lg text-info"></div>
        <div className="absolute inset-0 animate-pulse-glow opacity-50"></div>
      </div>
    </div>;
  }

  return (
    <div className="bg-base-300 home-page relative pt-12 sm:pt-16">
      {/* Background circuit effect */}
      <div className="absolute inset-0 bg-circuit-pattern opacity-5 pointer-events-none"></div>
      
      {/* Animated circuit lines */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="circuit-line w-full h-1 bg-primary/30 absolute top-[20%] left-0 animate-pulse"></div>
        <div className="circuit-line w-1 h-full bg-primary/30 absolute top-0 left-[30%] animate-pulse" style={{animationDelay: '0.5s'}}></div>
        <div className="circuit-line w-full h-1 bg-secondary/30 absolute top-[70%] left-0 animate-pulse" style={{animationDelay: '1s'}}></div>
        <div className="circuit-line w-1 h-full bg-secondary/30 absolute top-0 left-[80%] animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        <div className="text-center mb-10">
          <div className="glitch-container inline-block mb-2">
            <h1 
              data-text="ARENA DE DESAFIOS"
              className="text-3xl sm:text-5xl font-bold text-primary font-tech tracking-wider"
            >
              ARENA DE DESAFIOS
            </h1>
          </div>
          <div className="h-1 w-20 bg-primary mx-auto mt-3"></div>
          <p className="text-sm sm:text-lg text-base-content/90 mt-4 font-tech-secondary">
            MELHORE SUAS HABILIDADES DE PROGRAMAÇÃO COM DESAFIOS INTERATIVOS
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Challenge Section */}
          <div className="lg:col-span-3">
            {challenges.map((challenge) => (
              <div key={challenge.id} className="card bg-base-100/90 backdrop-blur-sm shadow-xl border border-primary/20 hover:shadow-primary/20">
                <div className="card-body">
                  <div className="challenge-display">
                    <ChallengeDisplay
                      title={challenge.title}
                      description={challenge.description}
                      language={challenge.language}
                      eloBase={challenge.elo_rating}
                    />
                  </div>

                  <div className="challenge-input">
                    <ChallengeInput
                      userAnswer={userAnswer}
                      setUserAnswer={setUserAnswer}
                      isLoading={isLoading}
                      feedback={feedback}
                      fontSize={14}
                      fontFamily="'Monospace'"
                      onSubmit={handleSubmit}
                      onNext={handleNext}
                    />
                  </div>

                  {feedback.isVisible && (
                    <ChallengeFeedback
                      feedback={feedback}
                      userStats={userProfile}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Profile & Ranking Section */}
          <div className="lg:col-span-1">
            <div className="card bg-base-100/90 backdrop-blur-sm shadow-xl mb-6 profile-card border border-primary/20 hover:shadow-primary/20">
              <div className="card-body items-center text-center py-4 px-3">
                <div className="avatar">
                  <div className="w-16 sm:w-20 rounded-full ring ring-primary ring-offset-base-100 ring-offset-2">
                    {userProfile?.avatar_url ? (
                      <img 
                        src={userProfile.avatar_url} 
                        alt={userProfile.username || 'Profile'} 
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <div className="bg-base-300 text-base-content rounded-full w-full h-full flex items-center justify-center">
                        <FaUser size={24} />
                      </div>
                    )}
                  </div>
                </div>
                <h2 className="card-title mt-3 text-primary text-base">
                  {userProfile?.username 
                    ? (userProfile.username.length > 15 
                      ? userProfile.username.substring(0, 15) + '...' 
                      : userProfile.username)
                    : 'User'
                  }
                </h2>
                <p className="text-sm">Nível {Math.floor((userProfile?.elo_rating || 1000) / 100)}</p>
                <p className="text-primary font-bold text-xs neon-text">
                  {userProfile?.rank || 'Ferro'}
                </p>
                <p className="text-xs text-base-content/90">
                  ELO {userProfile?.elo_rating || 1000}
                </p>
              </div>
            </div>

            <div className="ranking-list">
              <RankingList 
                users={rankingUsers}
                currentUserId={session?.user?.id}
                preview={true}
                context="challenges"
              />
            </div>
          </div>
        </div>
      </div>
    <br></br>
    </div>
  );
}